import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Check, X, RotateCcw, Clock, Pencil } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { fmt12, allDaySlots, nextSlot, actedHM } from "@/lib/api";

const QUICK = [
  { m: 15, label: "15m" },
  { m: 30, label: "30m" },
  { m: 60, label: "1h" },
  { m: 180, label: "3h" },
];

const Wide = ({ testid, onClick, tone, icon: Icon, label }) => {
  const tones = {
    green: "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
    red: "bg-red-500/15 text-red-300 ring-red-500/30",
    slate: "bg-slate-800/70 text-slate-200 ring-slate-700",
  };
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`flex h-13 w-full items-center justify-center gap-2 whitespace-nowrap rounded-full px-4 py-3.5 text-[15px] font-semibold ring-1 transition-transform duration-150 active:scale-95 ${tones[tone]}`}
    >
      <Icon className="h-[18px] w-[18px] shrink-0" /> {label}
    </button>
  );
};

export const DoseDetail = ({ item, status, onAction, onClose, hideSnooze, dateLabel }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const slots = useMemo(() => allDaySlots(), []);
  const [slot, setSlot] = useState("");
  const [actedTime, setActedTime] = useState("");

  useEffect(() => {
    setSlot(nextSlot());
    setActedTime(actedHM(item));
  }, [item]);

  const snoozed = status === "snoozed" || status === "snooze-due";
  const done = status === "taken" || status === "skipped";

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[350px] rounded-2xl border-slate-800 bg-slate-950/95 backdrop-blur-xl"
        data-testid="dose-sheet"
      >
        <DialogHeader className="min-w-0 text-left">
          <DialogTitle className="flex min-w-0 items-start gap-2 text-xl font-bold tracking-tight">
            <span className="min-w-0 flex-1 break-words">{item?.name}</span>
            <button
              data-testid="dose-edit-medicine-btn"
              onClick={() => navigate("/medicines", { state: { editMedId: item.med_id, from: location.pathname } })}
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-slate-400 transition-colors duration-200 hover:bg-slate-800 hover:text-sky-300 active:scale-90"
            >
              <Pencil className="h-4 w-4" />
            </button>
          </DialogTitle>
        </DialogHeader>

        {item?.notes && (
          <p
            className="-mt-2 border-b border-slate-800 pb-2.5 text-[16px] leading-snug text-slate-200"
            data-testid="dose-sheet-notes"
          >
            {item.notes}
          </p>
        )}

        <DialogDescription className="-mt-1 text-[15px] text-slate-400">
          {snoozed && item?.snooze_until
            ? `${fmt12(item.snooze_until)} (Original: ${fmt12(item.time)})`
            : `Scheduled ${fmt12(item?.time)}`}
          {dateLabel ? ` · ${dateLabel}` : ""}
        </DialogDescription>

        <div className="space-y-3">
          {done ? (
            <>
              <div className="flex gap-2">
                <Wide
                  testid="sheet-taken-btn"
                  tone="green"
                  icon={Check}
                  label="Taken"
                  onClick={() => onAction("taken")}
                />
                <Wide testid="sheet-skip-btn" tone="red" icon={X} label="Skip" onClick={() => onAction("skipped")} />
              </div>
              <div>
                <p className="mb-2 text-[14px] text-slate-500">
                  {status === "taken" ? "Taken at" : "Skipped at"}
                </p>
                <div className="flex gap-2">
                  <input
                    data-testid="acted-time-input"
                    type="time"
                    value={actedTime}
                    onChange={(e) => setActedTime(e.target.value)}
                    className="h-12 flex-1 rounded-xl border border-slate-800 bg-slate-900/70 px-3 text-[15px] text-slate-100"
                  />
                  <button
                    data-testid="acted-time-save-btn"
                    disabled={!actedTime}
                    onClick={() => onAction(status, { acted_hm: actedTime })}
                    className="h-12 shrink-0 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-5 text-[14px] font-semibold text-white disabled:opacity-40 active:scale-95"
                  >
                    Update
                  </button>
                </div>
              </div>
              <Wide
                testid="sheet-undo-btn"
                tone="slate"
                icon={RotateCcw}
                label="Undo"
                onClick={() => onAction("pending")}
              />
            </>
          ) : (
            <>
              <div className="flex gap-2">
                <Wide
                  testid="sheet-taken-btn"
                  tone="green"
                  icon={Check}
                  label="Taken"
                  onClick={() => onAction("taken")}
                />
                <Wide testid="sheet-skip-btn" tone="red" icon={X} label="Skip" onClick={() => onAction("skipped")} />
              </div>

              {!hideSnooze && (
                <>
                  <div>
                    <p className="mb-2 text-[14px] text-slate-500">Snooze by</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {QUICK.map((q) => (
                    <button
                      key={q.m}
                      data-testid={`sheet-snooze-${q.m}`}
                      onClick={() => onAction("snoozed", { snooze_minutes: q.m })}
                      className="h-11 rounded-xl border border-amber-500/25 bg-amber-500/10 text-[14px] font-semibold text-amber-200 active:scale-95"
                    >
                      {q.label}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="mb-2 text-[14px] text-slate-500">Snooze to a time today</p>
                <div className="flex gap-2">
                  <Select value={slot} onValueChange={setSlot}>
                    <SelectTrigger
                      data-testid="snooze-time-select"
                      className="h-12 flex-1 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
                    >
                      <SelectValue placeholder="Pick a time" />
                    </SelectTrigger>
                    <SelectContent className="max-h-64 border-slate-800 bg-slate-950">
                      {slots.map((s) => (
                        <SelectItem
                          key={s.value}
                          value={s.value}
                          disabled={s.past}
                          data-testid={`snooze-slot-${s.value}`}
                          className={`text-[15px] ${s.past ? "text-slate-600 opacity-50" : ""}`}
                        >
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <button
                    data-testid="snooze-custom-btn"
                    disabled={!slot}
                    onClick={() => onAction("snoozed", { snooze_until: slot })}
                    className="h-12 shrink-0 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-5 text-[14px] font-semibold text-white disabled:opacity-40 active:scale-95"
                  >
                    Set
                  </button>
                </div>
              </div>
                </>
              )}
            </>
          )}

          {snoozed && (
            <Wide
              testid="sheet-reset-snooze-btn"
              tone="slate"
              icon={Clock}
              label={`Reset Snooze (${fmt12(item?.time)})`}
              onClick={() => onAction("pending")}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
